/** Build human-readable address lines for v3 UI (checkout picker, nav hints, etc.). */

import { getAreaBilingualLabel } from './bilingualLabel';

export type AddressLike = Record<string, unknown>;

function readAreaLabel(address: AddressLike): string {
  const area = address.area;
  if (address.area_name != null && String(address.area_name).trim() !== '') {
    return String(address.area_name).trim();
  }
  if (area != null && typeof area === 'object' && !Array.isArray(area)) {
    const label = getAreaBilingualLabel(area as Record<string, unknown>);
    if (label.trim() !== '') {
      return label.trim();
    }
  }
  if (typeof area === 'string' && area.trim() !== '') {
    return area.trim();
  }
  if (address.governorate != null && String(address.governorate).trim() !== '') {
    return String(address.governorate).trim();
  }
  return '';
}

/** Multi-line address detail (area/block, street, floor/flat, landmark). */
export function buildV3AddressDetailLines(
  address: AddressLike,
  t: (key: string) => string,
): string[] {
  const area_label = readAreaLabel(address);
  const lines: string[] = [];

  if (area_label || address.block) {
    lines.push(
      [area_label, address.block ? `${t('address.block')} ${address.block}` : ''].filter(Boolean).join(', '),
    );
  }
  if (address.street || address.house_name) {
    lines.push(
      [
        address.street ? `${t('address.street')} ${address.street}` : '',
        address.house_name ? `${t('address.house')} ${address.house_name}` : '',
      ]
        .filter(Boolean)
        .join(', '),
    );
  }
  if (address.floor || address.flat) {
    lines.push(
      [
        address.floor ? `${t('address.floor')} ${address.floor}` : '',
        address.flat ? `${t('address.flat')} ${address.flat}` : '',
      ]
        .filter(Boolean)
        .join(', '),
    );
  }
  if (address.landmark) {
    lines.push(`${t('address.landmark')}: ${address.landmark}`);
  }

  if (lines.length === 0) {
    const fallback = [address.full_address, address.address]
      .map(v => (v != null ? String(v).trim() : ''))
      .find(v => v.length > 0);
    if (fallback) {
      lines.push(fallback);
    }
  }

  return lines.length > 0 ? lines : ['—'];
}

/** Single-line summary for compact checkout rows. */
export function formatAddressSummaryLine(address: AddressLike, t: (key: string) => string): string {
  return buildV3AddressDetailLines(address, t)
    .filter(line => line !== '—')
    .join(' · ');
}
